// src/App.js
import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { BrowserRouter, Navigate, Routes, Route, useLocation } from 'react-router-dom';

// Layouts
import AppLayout from './components/AppLayout';

// Public Pages
import Landing from './pages/Landing';
import AccountAccess from './pages/AccountAccess';

// Internal/Protected Pages
import DashboardMain from './pages/DashboardMain';
import ClientIntakeCanadianCriminal from './pages/ClientIntakeCanadianCriminal';
import CaseChatCopilot from './pages/CaseChatCopilot';
import Steps from './pages/Steps';
import ResearchWorkspace from './pages/ResearchWorkspace';
import DraftingAutomation from './pages/DraftingAutomation';
import SettingsPage from './pages/SettingsPage';
import MatterWorkspace from './pages/MatterWorkspace';
import PracticeAdministration from './pages/PracticeAdministration';
import { getCurrentUser } from './features/account/api';
import { ACTIVE_MATTER_KEY } from './edition';

function ProtectedWorkspace() {
  const location = useLocation();
  const [access, setAccess] = useState(process.env.NODE_ENV === 'test' ? 'allowed' : 'checking');
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    if (process.env.NODE_ENV === 'test') return undefined;
    let active = true;
    getCurrentUser()
      .then(body => { if (active) setAccess(body.authenticated || body.developmentDemo ? 'allowed' : 'denied'); })
      .catch(() => { if (active) setAccess('error'); });
    return () => { active = false; };
  }, [retryToken]);

  if (access === 'checking') return <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}><CircularProgress aria-label="Checking firm access" /></Box>;
  if (access === 'error') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: 'background.default', p: 2 }}>
        <Stack spacing={2} sx={{ width: '100%', maxWidth: 520 }}>
          <Alert severity="warning"><Typography fontWeight={750}>The workspace could not be reached.</Typography>Check your connection and try again. You have not been signed out.</Alert>
          <Button variant="contained" onClick={() => { setAccess('checking'); setRetryToken(value => value + 1); }}>Try again</Button>
          <Button component="a" href="/">Return to sign in</Button>
        </Stack>
      </Box>
    );
  }
  if (access === 'denied') {
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={`/?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }
  return <AppLayout />;
}

function ResearchRouteRedirect({ view }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('view', view);
  return <Navigate to={`/research?${params.toString()}`} replace />;
}

function MatterToolRedirect({ destination, stage }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const activeMatterId = localStorage.getItem(ACTIVE_MATTER_KEY);
  if (!params.get('matterId') && activeMatterId) params.set('matterId', activeMatterId);
  if (stage) params.set('stage', stage);
  return <Navigate to={`${destination}${params.size ? `?${params.toString()}` : ''}`} replace />;
}

export default function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        {/* Public Landing Page */}
        <Route path="/" element={<Landing />} />
        <Route path="/account" element={<Navigate to="/" replace />} />
        <Route path="/register" element={<AccountAccess />} />
        <Route path="/recover" element={<AccountAccess />} />
        <Route path="/claim-account" element={<AccountAccess />} />

        {/* Protected/Internal App - all inside AppLayout */}
        <Route element={<ProtectedWorkspace />}>
          <Route path="/dashboard" element={<DashboardMain />} />
          <Route path="/practice" element={<PracticeAdministration />} />
          <Route path="/matters" element={<MatterWorkspace />} />
          <Route path="/intake" element={<ClientIntakeCanadianCriminal />} />
          <Route path="/copilot" element={<CaseChatCopilot />} />
          <Route path="/playbooks" element={<Steps />} />
          <Route path="/research" element={<ResearchWorkspace />} />
          <Route path="/explorer" element={<ResearchRouteRedirect view="decisions" />} />
          <Route path="/archive" element={<ResearchRouteRedirect view="decisions" />} />
          <Route path="/case-insights" element={<ResearchRouteRedirect view="patterns" />} />
          <Route path="/drafting" element={<DraftingAutomation />} />
          <Route path="/library" element={<MatterToolRedirect destination="/drafting" />} />
          <Route path="/templates" element={<MatterToolRedirect destination="/drafting" />} />
          <Route path="/motions" element={<MatterToolRedirect destination="/playbooks" stage="motions-charter" />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
